import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { countBadge, countWords, mayReadAlerts } from './alertsView';

/**
 * The Alerts count (ops insights Phase 7 spec §6; MAP G-A1): critical and high alerts that are
 * Live, New (nobody on them), not snoozed and not muted (`alert.count`). It sits on the
 * sidebar's Alerts item and on the phone's More button, never on the bell (the bell is a
 * person's own notifications).
 *
 * The bell's recipe (`shell/Bell.tsx`): asked every 60 s while the browser tab is visible, and
 * again when it comes back (TanStack's focus manager watches `visibilitychange` alone). Each
 * chrome that draws it calls this once, and only the one SHOWN for this viewport polls
 * (`polls`): the sidebar at a desktop, the More button on a phone. Both read the one count.
 *
 * Owner and office only (`alert:read`): for any other role the read is never sent (`enabled`),
 * and there is no count to draw.
 */
export const ALERT_COUNT_POLL_MS = 60_000;

export function useAlertCount(polls: boolean): number | undefined {
  const { role } = useSession();
  const mayRead = mayReadAlerts(role);
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.alert.count.queryOptions(undefined, {
      refetchInterval: polls ? ALERT_COUNT_POLL_MS : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: polls ? 'always' : false,
    }),
    enabled: mayRead,
  });
  return mayRead ? q.data?.count : undefined;
}

/**
 * The count in words, for INSIDE the control it belongs to ("Alerts, 3 alerts need someone"):
 * the drawn badge is `aria-hidden`, so this is what a screen reader hears. Nothing at none.
 */
export function AlertCountWords({ count }: { count: number | undefined }) {
  const words = countWords(count);
  return words ? <span className="sr-only">, {words}</span> : null;
}

/**
 * The drawn badge, in the danger token (light and dark both redefine it). Positioned by the
 * caller (`className`): beside the sidebar item, or on the More button's icon.
 */
export function AlertCountBadge({
  count,
  className = '',
}: {
  count: number | undefined;
  className?: string;
}) {
  const badge = countBadge(count);
  if (!badge) return null;
  return (
    <span
      aria-hidden="true"
      data-alert-count={badge}
      className={`pointer-events-none flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--sw-danger-text)] px-1 text-[10px] font-bold leading-none tabular-nums text-surface ${className}`}
    >
      {badge}
    </span>
  );
}
